import { useEffect, useRef } from "react";
import type { WalterSnapshot } from "../core/types";

interface ParticleFieldProps {
  snapshot: WalterSnapshot;
  onFps?: (fps: number) => void;
}

interface Particle {
  x: number;
  y: number;
  z: number;
  drift: number;
  seed: number;
}

export function ParticleField({ snapshot, onFps }: ParticleFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return;

    let frame = 0;
    let last = performance.now();
    let fpsLast = last;
    let fpsFrames = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const eco = snapshotRef.current.settings.performanceMode === "eco" || reducedMotion;
    const particles: Particle[] = Array.from({ length: eco ? 26 : 48 }, (_, index) => ({
      x: ((index * 83) % 719) / 720,
      y: ((index * 149) % 719) / 720,
      z: 0.25 + ((index * 31) % 70) / 100,
      drift: 0.000025 + ((index * 7) % 12) / 500000,
      seed: index * 1.937,
    }));

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, eco ? 1 : 1.35);
      const width = Math.max(1, Math.floor(rect.width * dpr));
      const height = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    let lastPaint = 0;
    const render = (now: number) => {
      frame = requestAnimationFrame(render);
      const current = snapshotRef.current;
      const frameBudget = current.settings.performanceMode === "eco" || reducedMotion ? 42 : 32;
      if (now - lastPaint < frameBudget) return;
      lastPaint = now;
      const dt = Math.min(now - last, 50);
      last = now;
      fpsFrames += 1;
      if (now - fpsLast > 700) {
        onFps?.(Math.round((fpsFrames * 1000) / (now - fpsLast)));
        fpsFrames = 0;
        fpsLast = now;
      }

      const intensity = current.state === "sleeping" ? 0.08 : current.state === "thinking" || current.state === "tool" ? 1 : 0.42;
      const speed = reducedMotion ? 0 : current.state === "thinking" ? 1.9 : current.state === "tool" ? 2.5 : 0.7;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.save();
      context.scale(canvas.width, canvas.height);
      context.globalCompositeOperation = "screen";

      for (const particle of particles) {
        particle.y -= particle.drift * dt * speed;
        particle.x += Math.sin(now * 0.00016 + particle.seed) * 0.000012 * speed;
        if (particle.y < -0.04) particle.y = 1.04;
        const pulse = 0.5 + Math.sin(now * 0.001 + particle.seed) * 0.35;
        const alpha = intensity * particle.z * pulse * 0.34;
        const radius = (0.0009 + particle.z * 0.0018) * (current.state === "tool" ? 1.4 : 1);
        context.beginPath();
        context.fillStyle = `rgba(101, 245, 255, ${alpha})`;
        context.arc(particle.x, particle.y, radius, 0, Math.PI * 2);
        context.fill();
      }
      context.restore();
    };

    frame = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [onFps]);

  return <canvas ref={canvasRef} className="particle-field" aria-hidden="true" />;
}
